import { useCallback, useState } from 'react';
import { useAnalytics } from '../contexts/analytics/AnalyticsProvider';
import { trpc } from '../lib/trpc/trpc';

type FeedbackState = 'none' | 'like' | 'dislike' | 'submitted';

interface UseAIMessageFeedbackReturn {
  feedbackState: FeedbackState;
  handleThumbsUp: () => void;
  handleThumbsDown: () => void;
  handleSubmitFeedback: (feedback: string) => void;
}

export const useAIMessageFeedback = (
  messageId: string,
  isStreaming: boolean,
  onFeedbackSubmitted: (messageId: string) => void,
): UseAIMessageFeedbackReturn => {
  const [feedbackState, setFeedbackState] = useState<FeedbackState>('none');
  const { trackEvent } = useAnalytics();

  const handleThumbsUp = useCallback(async () => {
    if (isStreaming) return;

    setFeedbackState('like');

    console.log(
      '[useAIMessageFeedback] Submitting thumbs up for messageId:',
      messageId,
    );

    try {
      const result = await trpc.createChatMessageFeedback.mutate({
        chatMessageId: messageId,
        value: 'like',
        message: null,
      });
      console.log(
        '[useAIMessageFeedback] Thumbs up submitted successfully:',
        result,
      );
      trackEvent('TinyAI Thumbs Up', {
        message_id: messageId,
      });
      onFeedbackSubmitted(messageId);
    } catch (error) {
      console.error(
        '[useAIMessageFeedback] Failed to submit thumbs up feedback:',
        error,
      );
      setFeedbackState('none');
    }
  }, [messageId, isStreaming, onFeedbackSubmitted, trackEvent]);

  const handleThumbsDown = useCallback(() => {
    if (isStreaming) return;

    setFeedbackState('dislike');
  }, [isStreaming]);

  const handleSubmitFeedback = useCallback(
    async (feedback: string) => {
      setFeedbackState('submitted');

      console.log(
        '[useAIMessageFeedback] Submitting dislike for messageId:',
        messageId,
        'feedback:',
        feedback,
      );

      try {
        const result = await trpc.createChatMessageFeedback.mutate({
          chatMessageId: messageId,
          value: 'dislike',
          message: feedback || null,
        });
        console.log(
          '[useAIMessageFeedback] Dislike submitted successfully:',
          result,
        );
        trackEvent('TinyAI Thumbs Down', {
          message_id: messageId,
          feedback_message: feedback || undefined,
        });
        onFeedbackSubmitted(messageId);
      } catch (error) {
        console.error(
          '[useAIMessageFeedback] Failed to submit dislike feedback:',
          error,
        );
        setFeedbackState('none');
      }
    },
    [messageId, onFeedbackSubmitted, trackEvent],
  );

  return {
    feedbackState,
    handleThumbsUp,
    handleThumbsDown,
    handleSubmitFeedback,
  };
};
